import { useEffect, useMemo, useState } from 'react'
import { Inbox, Loader2, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useInbox, type InboxItem } from '@/hooks/use-inbox'
import { filterInboxTriageItems, inboxTriageTypeLabel } from '@/lib/inbox-thread-filters'
import { isThreadWorkspaceVisible } from '@/components/features/inbox/mappers'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { dispatchOpenInboxMain } from '@/components/features/inbox/inbox-folder-types'
import { useWorkspace } from '@/providers/workspace-provider'
import { workspaceListRowChrome } from '@/components/features/inbox/workspace-accent'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  homeSelectionKey,
  inboxDocumentId,
  type HomeContinuityProps,
  type HomeSelection,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'

const FEED_LIMIT = 5

function relativeTime(ts: string): string {
  const ms = Date.now() - new Date(ts).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(ts).toLocaleDateString()
}

function typeLabel(item: InboxItem): string {
  return inboxTriageTypeLabel(item.type)
}

function toSelection(item: InboxItem): HomeSelection {
  return { kind: 'inbox', item }
}

/** Home Inbox respects the same active-workspace scope as InboxPage / Chats. */
function isHomeInboxItemVisible(item: InboxItem, activeWorkspaceIds: string[]): boolean {
  const workspaceId =
    typeof item.meta?.workspaceId === 'string' && item.meta.workspaceId.trim()
      ? item.meta.workspaceId
      : 'personal'
  return isThreadWorkspaceVisible(
    workspaceId,
    activeWorkspaceIds,
    item.taggedOrganizationIds,
  )
}

interface HomeInboxFeedSectionProps {
  onInboxItemActivate?: (item: InboxItem) => void
  continuity?: HomeContinuityProps
}

export function HomeInboxFeedSection({
  onInboxItemActivate,
  continuity,
}: HomeInboxFeedSectionProps) {
  const { items, isLoading } = useInbox()
  const { activeWorkspaceIds, workspaces } = useWorkspace()
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    item: InboxItem
  } | null>(null)

  const triageItems = useMemo(() => {
    const triage = filterInboxTriageItems(items)
    return triage.filter((item) => isHomeInboxItemVisible(item, activeWorkspaceIds))
  }, [items, activeWorkspaceIds])

  const feedItems = useMemo(() => {
    const unread = triageItems.filter((i) => i.status === 'unread')
    const pool = unread.length > 0 ? unread : triageItems
    return pool.slice(0, FEED_LIMIT)
  }, [triageItems])

  const triageUnreadCount = useMemo(
    () => triageItems.filter((i) => i.status === 'unread').length,
    [triageItems],
  )

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const openInboxSidebar = () => {
    openSidebarMode('inbox')
    dispatchOpenInboxMain()
  }

  const selectItem = (item: InboxItem, opts?: { openPreview?: boolean }) => {
    continuity?.selectHome(toSelection(item), opts)
  }

  const openItem = (item: InboxItem) => {
    if (continuity) {
      continuity.openSelection(toSelection(item))
      return
    }
    if (onInboxItemActivate) {
      onInboxItemActivate(item)
      return
    }
    openInboxSidebar()
  }

  return (
    <HomeWidgetShell
      title="Inbox"
      icon={<Inbox />}
      data-testid={testIds.home.inboxFeed}
      className="min-h-[180px]"
      action={
        triageUnreadCount > 0 ? (
          <span className="text-xs font-medium text-foreground">{triageUnreadCount} unread</span>
        ) : null
      }
      footer={
        <Button variant="ghost" size="sm" className="w-full h-8" onClick={openInboxSidebar}>
          Open Inbox in sidebar
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openItem(contextMenu.item)}
          onPreview={() => selectItem(contextMenu.item, { openPreview: true })}
          addToChat={(() => {
            const docId = inboxDocumentId(contextMenu.item)
            return docId
              ? { id: docId, title: contextMenu.item.title }
              : null
          })()}
          workspaces={continuity?.workspaces ?? workspaces}
        />
      )}

      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && feedItems.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center flex-1">Inbox is empty</p>
      )}

      {!isLoading && feedItems.length > 0 && (
        <HomeFeedList>
          {feedItems.map((item) => {
            const tagIds = item.taggedOrganizationIds ?? []
            const itemWorkspaceId =
              tagIds.find((id) => workspaces.some((w) => w.id === id))
              ?? (typeof item.meta?.workspaceId === 'string' ? item.meta.workspaceId : undefined)
            const workspaceColor =
              typeof itemWorkspaceId === 'string'
                ? workspaces.find((w) => w.id === itemWorkspaceId)?.color ?? undefined
                : undefined
            const isSelected =
              continuity != null &&
              homeSelectionKey(toSelection(item)) === continuity.selectedKey
            const rowChrome = workspaceListRowChrome(workspaceColor, isSelected, {
              showRail:
                continuity?.showWorkspaceRails ?? activeWorkspaceIds.length > 1,
            })
            const metaParts = [typeLabel(item), item.source].filter(Boolean)
            return (
              <HomeFeedRow
                key={item.id}
                title={item.title}
                meta={metaParts.join(' · ')}
                trailing={relativeTime(item.timestamp)}
                selected={isSelected || (!continuity && item.status === 'unread')}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) {
                    selectItem(item)
                    openItem(item)
                    return
                  }
                  selectItem(item)
                }}
                onDoubleClick={() => {
                  selectItem(item)
                  openItem(item)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectItem(item)
                  setContextMenu({ x: e.clientX, y: e.clientY, item })
                }}
                className={rowChrome.className}
                style={rowChrome.style}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
